// src/components/games/NumberPuzzleGame.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiRefreshCw, FiClock, FiZap } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const NumberPuzzleGame = ({ onBack }) => {
  const [tiles, setTiles] = useState([]);
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [bestScore, setBestScore] = useState(() => {
    const saved = localStorage.getItem('numberPuzzleBest');
    return saved ? JSON.parse(saved) : null;
  });

  const createSolvedPuzzle = () => {
    return [...Array(15).keys()].map(i => i + 1).concat(null);
  };

  const isSolvable = (puzzle) => {
    let inversions = 0;
    const flatPuzzle = puzzle.filter(tile => tile !== null);
    
    for (let i = 0; i < flatPuzzle.length; i++) {
      for (let j = i + 1; j < flatPuzzle.length; j++) {
        if (flatPuzzle[i] > flatPuzzle[j]) {
          inversions++;
        }
      }
    }
    
    return inversions % 2 === 0;
  };

  const shufflePuzzle = useCallback(() => {
    let newPuzzle;
    do {
      newPuzzle = createSolvedPuzzle().sort(() => Math.random() - 0.5);
    } while (!isSolvable(newPuzzle) || JSON.stringify(newPuzzle) === JSON.stringify(createSolvedPuzzle()));
    
    return newPuzzle;
  }, []);

  const initGame = useCallback(() => {
    setTiles(shufflePuzzle());
    setMoves(0);
    setTime(0);
    setIsRunning(false);
    setGameWon(false);
  }, [shufflePuzzle]);

  useEffect(() => {
    initGame();
  }, [initGame]);

  useEffect(() => {
    let interval;
    if (isRunning && !gameWon) {
      interval = setInterval(() => {
        setTime(t => t + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, gameWon]);

  const checkWin = (newTiles) => {
    const solved = createSolvedPuzzle();
    return JSON.stringify(newTiles) === JSON.stringify(solved);
  };

  const getEmptyIndex = (tilesArray) => {
    return tilesArray.indexOf(null);
  };

  const canMove = (index, emptyIndex) => {
    const row = Math.floor(index / 4);
    const col = index % 4;
    const emptyRow = Math.floor(emptyIndex / 4);
    const emptyCol = emptyIndex % 4;

    return (
      (Math.abs(row - emptyRow) === 1 && col === emptyCol) ||
      (Math.abs(col - emptyCol) === 1 && row === emptyRow)
    );
  };

  const moveTile = (index) => {
    if (!isRunning && moves === 0) {
      setIsRunning(true);
    }

    const emptyIndex = getEmptyIndex(tiles);
    
    if (canMove(index, emptyIndex)) {
      const newTiles = [...tiles];
      [newTiles[index], newTiles[emptyIndex]] = [newTiles[emptyIndex], newTiles[index]];
      
      setTiles(newTiles);
      setMoves(m => m + 1);

      if (checkWin(newTiles)) {
        setGameWon(true);
        setIsRunning(false);
        
        const newScore = { moves: moves + 1, time };
        if (!bestScore || moves + 1 < bestScore.moves || (moves + 1 === bestScore.moves && time < bestScore.time)) {
          setBestScore(newScore);
          localStorage.setItem('numberPuzzleBest', JSON.stringify(newScore));
          toast.success('🎉 رقم قياسي جديد!');
        } else {
          toast.success('🎉 أحسنت! لقد أنهيت اللعبة');
        }
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 p-4">
      {/* Header */}
      <div className="max-w-lg mx-auto mb-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-md hover:shadow-lg transition font-bold text-gray-700"
          >
            <FiArrowLeft size={18} />
            <span className="text-sm">رجوع</span>
          </button>

          <h1 className="text-2xl font-black text-gray-900">لغز الأرقام</h1>

          <button
            onClick={initGame}
            className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-md hover:shadow-lg transition font-bold text-gray-700"
          >
            <FiRefreshCw size={18} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-green-200 text-center">
            <FiClock className="mx-auto mb-2 text-green-600" size={24} />
            <div className="text-2xl font-black text-gray-900">{formatTime(time)}</div>
            <div className="text-xs font-bold text-gray-600">الوقت</div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-emerald-200 text-center">
            <FiZap className="mx-auto mb-2 text-emerald-600" size={24} />
            <div className="text-2xl font-black text-gray-900">{moves}</div>
            <div className="text-xs font-bold text-gray-600">الحركات</div>
          </div>
        </div>

        {/* Best Score */}
        {bestScore && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 shadow-md border-2 border-yellow-200 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="text-2xl">🏆</div>
                <div>
                  <div className="text-xs font-bold text-gray-600">أفضل نتيجة</div>
                  <div className="text-sm font-black text-gray-900">
                    {bestScore.moves} حركة • {formatTime(bestScore.time)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Win Message */}
        <AnimatePresence>
          {gameWon && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -20 }}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl p-6 shadow-xl mb-6 text-center"
            >
              <div className="text-5xl mb-3">🎉</div>
              <h3 className="text-2xl font-black mb-2">مبروك!</h3>
              <p className="text-sm opacity-90 mb-4">
                أنهيت اللعبة في {moves} حركة و {formatTime(time)}
              </p>
              <button
                onClick={initGame}
                className="bg-white text-green-600 px-6 py-3 rounded-xl font-bold shadow-lg hover:bg-gray-100 transition"
              >
                العب مرة أخرى
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Game Board */}
      <div className="max-w-lg mx-auto">
        <div className="bg-white p-3 rounded-3xl shadow-2xl border-2 border-green-100">
          <div className="grid grid-cols-4 gap-2">
            {tiles.map((tile, index) => (
              <motion.button
                key={index}
                whileHover={tile && canMove(index, getEmptyIndex(tiles)) ? { scale: 1.05 } : {}}
                whileTap={tile && canMove(index, getEmptyIndex(tiles)) ? { scale: 0.95 } : {}}
                onClick={() => tile && moveTile(index)}
                className={`aspect-square rounded-xl font-black text-2xl sm:text-3xl flex items-center justify-center transition-all duration-200 ${
                  tile
                    ? canMove(index, getEmptyIndex(tiles))
                      ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg cursor-pointer hover:shadow-xl'
                      : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600 shadow-md'
                    : 'bg-transparent'
                }`}
                disabled={!tile || gameWon}
              >
                {tile}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-white rounded-2xl p-4 shadow-md border-2 border-gray-100">
          <h4 className="font-black text-gray-900 mb-2 text-sm">كيفية اللعب:</h4>
          <p className="text-xs text-gray-600 leading-relaxed">
            اضغط على الأرقام المجاورة للمربع الفارغ لتحريكها. رتب الأرقام من 1 إلى 15 بأقل عدد من الحركات والوقت!
          </p>
        </div>
      </div>
    </div>
  );
};

export default NumberPuzzleGame;